import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobProfileLegacy } from '~/shared/data-contract/job-profile';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export type UseUpdateJobProfileResult = UseMutationResult<
    JobProfileLegacy,
    Error,
    {
        readonly acceptLanguage: string;
        readonly architectureId: string;
        readonly jobItemType:
            | { readonly id: string; readonly type: 'job-level' }
            | { readonly id: string; readonly type: 'job-role' };
        readonly jobProfile: JobProfileLegacy;
        readonly version?: string;
        readonly versionComment?: string;
        readonly withAutoTranslation: boolean;
    }
>;

const mergeTranslations = (
    existingTranslations: JobProfileLegacy['translations'],
    newTranslations: JobProfileLegacy['translations'],
): JobProfileLegacy['translations'] => {
    const translationsMap = new Map(existingTranslations.map((translation) => [translation.language, translation]));
    // biome-ignore lint/suspicious/useIterableCallbackReturn: suppressed due to migration
    newTranslations.forEach((translation) => translationsMap.set(translation.language, translation));

    return [...translationsMap.values()];
};

export const useUpdateJobProfile = (): UseUpdateJobProfileResult => {
    const { updateJobProfile } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ acceptLanguage, jobProfile, versionComment, withAutoTranslation }) => {
            const result = await updateJobProfile({
                acceptLanguage,
                jobProfile,
                versionComment,
                withAutoTranslation,
            });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: async ({ architectureId, jobItemType, jobProfile, version }) => {
            const queryKey =
                jobItemType.type === 'job-level'
                    ? jobArchitectureKeys.level(architectureId, jobItemType.id, version)
                    : jobArchitectureKeys.role(architectureId, jobItemType.id, version);

            await queryClient.cancelQueries({ queryKey });

            queryClient.setQueryData<JobLevelDetails | JobRoleDetails>(queryKey, (old) => {
                if (is.undefined(old?.jobProfile)) {
                    return old;
                }

                return {
                    ...old,
                    jobProfile: {
                        ...old.jobProfile,
                        ...jobProfile,
                        translations: mergeTranslations(old.jobProfile.translations, jobProfile.translations),
                    },
                };
            });
        },
        onSuccess: (_, { architectureId, jobItemType }) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.role(architectureId, jobItemType.id),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.level(architectureId, jobItemType.id),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.levels(architectureId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.roles(architectureId),
            });
        },
    });
};
